/**
 * Shared crop for announcement / product-splash images.
 * Preview and live shop both call buildAnnouncementImageFitStyle so they match.
 * Static crop only: object-fit + focal point + zoom. No ken-burns.
 */

export const ANNOUNCEMENT_IMAGE_FIT_COVER = 'cover';
export const ANNOUNCEMENT_IMAGE_FIT_CONTAIN = 'contain';
export const ANNOUNCEMENT_IMAGE_SCALE_MIN = 1;
export const ANNOUNCEMENT_IMAGE_SCALE_MAX = 2.5;

export const ANNOUNCEMENT_IMAGE_FOCAL_PRESETS = {
    center: { x: 50, y: 50 },
    top: { x: 50, y: 0 },
    bottom: { x: 50, y: 100 },
};

export const ANNOUNCEMENT_IMAGE_FIT_DEFAULTS = {
    fit: ANNOUNCEMENT_IMAGE_FIT_COVER,
    scale: 1,
    x: 50,
    y: 50,
};

const FIELD_NAMES = {
    image: {
        fit: 'imageFit',
        scale: 'imageScale',
        x: 'imagePosX',
        y: 'imagePosY',
    },
    backgroundImage: {
        fit: 'backgroundImageFit',
        scale: 'backgroundImageScale',
        x: 'backgroundImagePosX',
        y: 'backgroundImagePosY',
    },
};

function clamp(n, min, max) {
    if (!Number.isFinite(n)) return min;
    return Math.min(max, Math.max(min, n));
}

function round1(n) {
    return Math.round(n * 10) / 10;
}

export function announcementImageFitFieldNames(kind) {
    return FIELD_NAMES[kind] || FIELD_NAMES.image;
}

export function normalizeAnnouncementImageFit(raw = {}) {
    const fitRaw = String(raw.fit || '').trim().toLowerCase();
    const fit = fitRaw === ANNOUNCEMENT_IMAGE_FIT_CONTAIN
        ? ANNOUNCEMENT_IMAGE_FIT_CONTAIN
        : ANNOUNCEMENT_IMAGE_FIT_COVER;
    const scaleRaw = Number(raw.scale);
    const scale = Number.isFinite(scaleRaw)
        ? round1(clamp(scaleRaw, ANNOUNCEMENT_IMAGE_SCALE_MIN, ANNOUNCEMENT_IMAGE_SCALE_MAX))
        : ANNOUNCEMENT_IMAGE_FIT_DEFAULTS.scale;
    const xRaw = Number(raw.x);
    const yRaw = Number(raw.y);
    const x = Number.isFinite(xRaw) ? Math.round(clamp(xRaw, 0, 100)) : ANNOUNCEMENT_IMAGE_FIT_DEFAULTS.x;
    const y = Number.isFinite(yRaw) ? Math.round(clamp(yRaw, 0, 100)) : ANNOUNCEMENT_IMAGE_FIT_DEFAULTS.y;
    return { fit, scale, x, y };
}

export function resolveAnnouncementImageFitFromDoc(data = {}, kind = 'image') {
    const names = announcementImageFitFieldNames(kind);
    if (!data || typeof data !== 'object') {
        return { ...ANNOUNCEMENT_IMAGE_FIT_DEFAULTS };
    }
    return normalizeAnnouncementImageFit({
        fit: data[names.fit],
        scale: data[names.scale],
        x: data[names.x],
        y: data[names.y],
    });
}

export function announcementImageFitToDocFields(kind, fit) {
    const names = announcementImageFitFieldNames(kind);
    const n = normalizeAnnouncementImageFit(fit);
    return {
        [names.fit]: n.fit,
        [names.scale]: n.scale,
        [names.x]: n.x,
        [names.y]: n.y,
    };
}

export function matchAnnouncementImageFocalPreset(fit) {
    const n = normalizeAnnouncementImageFit(fit);
    const entries = Object.entries(ANNOUNCEMENT_IMAGE_FOCAL_PRESETS);
    for (let i = 0; i < entries.length; i += 1) {
        const [name, pt] = entries[i];
        if (pt.x === n.x && pt.y === n.y) return name;
    }
    return '';
}

export function applyAnnouncementImageFocalPreset(fit, presetName) {
    const pt = ANNOUNCEMENT_IMAGE_FOCAL_PRESETS[presetName];
    if (!pt) return normalizeAnnouncementImageFit(fit);
    return normalizeAnnouncementImageFit({ ...fit, x: pt.x, y: pt.y });
}

/**
 * Inline style for an <img> inside an overflow-hidden frame.
 * object-fit + object-position crop the frame; scale zooms from the focal point.
 */
export function buildAnnouncementImageFitStyle(raw) {
    const n = normalizeAnnouncementImageFit(raw);
    const pos = `${n.x}% ${n.y}%`;
    return {
        objectFit: n.fit,
        objectPosition: pos,
        transform: `scale(${n.scale})`,
        transformOrigin: pos,
    };
}
